import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { Kpis, Panel, BarsH, Stack100 } from '../../components/charts';

/* ============================================================
   Dashboard delay.

   Dua angka dibawa berdampingan sepanjang halaman ini: total slip,
   dan bagian yang BELUM DIJELASKAN. Analisis yang membagi sisa itu
   rata ke peristiwa yang kebetulan ada terlihat lebih rapi dan
   jauh lebih mudah dibantah.
   ============================================================ */

export const METHOD_LABEL: Record<string, string> = {
  WINDOWS_TIA: 'Windows / Time Impact',
  AS_PLANNED_VS_AS_BUILT: 'As-planned vs as-built',
  COLLAPSED_AS_BUILT: 'Collapsed as-built',
  IMPACTED_AS_PLANNED: 'Impacted as-planned',
};

export const ENTITLEMENT_LABEL: Record<string, string> = {
  EXCUSABLE_COMPENSABLE: 'Berhak waktu & biaya',
  EXCUSABLE_ONLY: 'Berhak waktu saja',
  NON_EXCUSABLE: 'Tidak berhak',
};

export const CAUSE_LABEL: Record<string, string> = {
  DESIGN_CHANGE: 'Perubahan desain',
  LATE_ACCESS: 'Akses terlambat',
  OWNER_INSTRUCTION: 'Instruksi pemilik',
  SUBCONTRACTOR: 'Subkontraktor',
  WEATHER: 'Cuaca',
  UTILITIES: 'Utilitas',
  OTHER: 'Lainnya',
};

type Summary = {
  analyses: {
    id: string;
    title: string;
    method: string;
    status: string;
    createdAt: string;
    issuedAt: string | null;
    _count: { windows: number };
  }[];
  events: {
    id: string;
    eventNo: string;
    title: string;
    startedAt: string;
    endedAt: string | null;
    cause: string;
    responsibility: string;
    _count: { variations: number; ncrs: number; attributions: number };
  }[];
  latest: {
    id: string;
    title: string;
    method: string;
    status: string;
    totals: {
      excusableCompensable: number;
      excusableOnly: number;
      nonExcusable: number;
      total: number;
      concurrentDays: number;
      totalExcludingConcurrent: number;
      totalSlip: number;
      unexplained: number;
    };
    mitigationTotals: { agreedDays: number; proposedDays: number; agreedCost: number; proposedCost: number };
    windows: { windowNo: number; slipDays: number; driving: string | null; unexplained: number }[];
  } | null;
  methods: {
    method: string;
    strength: string;
    limitations: string;
    readiness: { ok: boolean; missing: string[] };
  }[];
};

export function Dashboard({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Summary>(`/delay/summary?projectId=${projectId}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the delay summary.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const l = data.latest;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Forensic Delay Analysis</h1>
          <p>Ke mana waktunya pergi, siapa yang menanggung risikonya, dan apa yang masih bisa dipulihkan.</p>
        </div>
      </div>

      {data.analyses.length === 0 ? (
        <Empty
          title="Belum ada analisis"
          body="Catat peristiwa keterlambatannya lebih dulu, lalu buat analisis dan pilih metodenya. Jendela dibentuk otomatis dari pasangan versi programme yang diterima."
          action={
            <button className="btn btn-primary" onClick={() => nav('/events')}>
              Buka peristiwa
            </button>
          }
        />
      ) : (
        l && (
          <>
            <Kpis
              items={[
                { label: 'Total slip', value: `${l.totals.totalSlip} hari`, note: METHOD_LABEL[l.method] ?? l.method },
                {
                  label: 'Berhak waktu & biaya',
                  value: `${l.totals.excusableCompensable} hari`,
                  note: 'excusable & compensable',
                },
                {
                  label: 'Belum dijelaskan',
                  value: `${l.totals.unexplained} hari`,
                  note: l.totals.unexplained > 0 ? 'belum dikaitkan ke peristiwa mana pun' : 'seluruh slip terjelaskan',
                  tone: l.totals.unexplained > 0 ? 'warn' : 'good',
                },
                {
                  label: 'Konkuren',
                  value: `${l.totals.concurrentDays} hari`,
                  note: l.totals.concurrentDays > 0 ? 'berjalan bersamaan, ditandai bukan dilipat' : 'tidak ada',
                  tone: l.totals.concurrentDays > 0 ? 'warn' : undefined,
                },
              ]}
            />

            <div className="grid-2">
              <Panel title="Slip per jendela" note="Diukur terhadap programme yang berlaku saat itu, bukan terhadap satu baseline yang dibaca mundur.">
                {l.windows.length === 0 ? (
                  <p className="hint">Jendela belum dibentuk.</p>
                ) : (
                  <BarsH
                    suffix=" h"
                    rows={l.windows.map((w) => ({
                      label: `W${w.windowNo}${w.driving ? ` · ${w.driving}` : ''}`,
                      value: w.slipDays,
                      tone: w.slipDays > 14 ? 'crit' : w.slipDays > 0 ? 'warn' : 'good',
                    }))}
                  />
                )}
              </Panel>

              <Panel title="Entitlement" note="Waktu, uang, atau tidak keduanya — tiga kategori yang menentukan siapa membayar.">
                {l.totals.total === 0 ? (
                  <p className="hint">Belum ada hari yang dikaitkan ke peristiwa.</p>
                ) : (
                  <>
                    <Stack100
                      suffix=" h"
                      slices={[
                        { name: ENTITLEMENT_LABEL.EXCUSABLE_COMPENSABLE!, value: l.totals.excusableCompensable, color: 'var(--series-1)' },
                        { name: ENTITLEMENT_LABEL.EXCUSABLE_ONLY!, value: l.totals.excusableOnly, color: 'var(--series-2)' },
                        { name: ENTITLEMENT_LABEL.NON_EXCUSABLE!, value: l.totals.nonExcusable, color: 'var(--text-muted)' },
                      ]}
                    />
                    {l.totals.concurrentDays > 0 && (
                      <p className="panel-foot">
                        {l.totals.concurrentDays} hari berjalan bersamaan dengan keterlambatan pihak
                        lain. Angkanya tetap masuk total di atas dan dilaporkan terpisah — melipatnya
                        ke salah satu sisi berarti memihak tanpa mengatakannya.
                      </p>
                    )}
                  </>
                )}
              </Panel>
            </div>

            {(l.mitigationTotals.agreedDays > 0 || l.mitigationTotals.proposedDays > 0) && (
              <Panel title="Pemulihan" note="Usulan dipisahkan dari yang sudah disepakati." wide>
                <Kpis
                  items={[
                    { label: 'Disepakati', value: `${l.mitigationTotals.agreedDays} hari` },
                    { label: 'Baru diusulkan', value: `${l.mitigationTotals.proposedDays} hari` },
                    {
                      label: 'Sisa slip setelah pemulihan disepakati',
                      value: `${l.totals.totalSlip - l.mitigationTotals.agreedDays} hari`,
                    },
                  ]}
                />
              </Panel>
            )}
          </>
        )
      )}

      <Panel title="Analisis" note="Satu proyek boleh punya beberapa analisis dengan metode berbeda." wide>
        {data.analyses.length === 0 ? (
          <p className="hint">Belum ada.</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Judul</th>
                  <th>Metode</th>
                  <th>Jendela</th>
                  <th>Status</th>
                  <th>Dibuat</th>
                </tr>
              </thead>
              <tbody>
                {data.analyses.map((a) => (
                  <tr key={a.id} onClick={() => nav(`/analyses/${a.id}`)}>
                    <td className="num">{a.title}</td>
                    <td>{METHOD_LABEL[a.method] ?? a.method}</td>
                    <td>{a._count.windows}</td>
                    <td>
                      <span className={`chip ${a.status === 'ISSUED' ? 'good' : ''}`}>{a.status}</span>
                    </td>
                    <td>{fmtDate(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Metode dan kesiapan datanya"
        note="Metode yang datanya belum ada ditolak lebih awal — angka dari data yang tidak ada terlihat sama meyakinkannya."
        wide
      >
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Metode</th>
                <th>Kekuatan</th>
                <th>Kelemahan yang wajib dilaporkan</th>
                <th>Siap</th>
              </tr>
            </thead>
            <tbody>
              {data.methods.map((m) => (
                <tr key={m.method} className="no-click">
                  <td className="num">{METHOD_LABEL[m.method] ?? m.method}</td>
                  <td className="hint">{m.strength}</td>
                  <td className="hint">{m.limitations}</td>
                  <td>
                    {m.readiness.ok ? (
                      <span className="chip good">Siap</span>
                    ) : (
                      <span className="chip warn" title={m.readiness.missing.join(', ')}>
                        Belum
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
